'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { PanelLeftOpen } from 'lucide-react';
import { useSidebarStore } from '@/shared/stores/sidebar-store';

interface DesktopShellProps {
  sidebar: ReactNode;
  children: ReactNode;
  /**
   * Render the brand teal hero wash full-bleed behind BOTH columns (project
   * home only). The frosted-glass sidebar then blurs the teal beneath it, so
   * the wash reads as one continuous gradient flowing under the navigation —
   * mirroring Apple's `backgroundExtensionEffect`. On the home page the page's
   * own in-content wash (`ProjectHeroSection`) is hidden at `lg+` so this is
   * the single source; below `lg` the shell/sidebar don't exist and the page
   * wash takes over.
   */
  showTopWash?: boolean;
  /**
   * Mobile passthrough (< lg). Renders the SAME wrapper element tree but with
   * `display: contents`, so the boxes vanish (visually identical to rendering
   * `children` bare) WHILE keeping `children` at a stable position in the React
   * tree across the breakpoint. Without this, crossing `lg` swapped the tree
   * shape and React unmounted+remounted the whole content subtree on resize —
   * destroying any in-flight local state (e.g. the angle upload/validation
   * modal). The sidebar and hero wash are skipped in this mode.
   */
  bare?: boolean;
}

const SIDEBAR_WIDTH_EXPANDED = 213;

/**
 * Left gutter reserved on the content column while the sidebar is collapsed, so
 * the page's leading content clears the floating top-left sidebar-reveal button
 * (see the button in the shell body). Must stay in sync with the Tailwind
 * `pl-14` applied to the content row (14 × 4px = 56px).
 */
const COLLAPSED_LEFT_GUTTER = 56;

/**
 * Brand teal hero wash (H=177°, S=90%), top → transparent. Shared by the
 * mobile `ProjectHeroSection` header and this shell's full-bleed desktop
 * backdrop so both fade identically (DRY — single gradient definition).
 */
export const HERO_WASH_GRADIENT =
  'linear-gradient(to bottom, rgba(64,232,223,0.5) 0%, rgba(64,232,223,0) 100%)';

/**
 * Distance (px) from the viewport's left edge to the content column's left edge
 * while the sidebar is shown: sidebar `ml-4` (16) + width + `gap-4` (16). Used
 * by body-portaled, viewport-fixed overlays (the stacked settings drill-down)
 * that can't inherit the `--content-inset-left` CSS var to align with the
 * content column instead of covering the sidebar.
 */
export const CONTENT_COLUMN_LEFT = 16 + SIDEBAR_WIDTH_EXPANDED + 16;

/**
 * Shared desktop shell used by Dashboard, Project, History and Account layouts.
 * Provides the outer container, max-width frame and the fixed-width left
 * sidebar column. Caller passes the sidebar content and the right-hand columns
 * (main content, optional middle panel, secondary sidebars) as `children`.
 *
 * Collapse behavior mirrors the Apple app (not Android): collapsing hides the
 * sidebar entirely and navigation moves to the floating bottom bar
 * (`MobileNavigation`), rather than shrinking into a compact icon rail.
 */
export function DesktopShell({
  sidebar,
  children,
  showTopWash = false,
  bare = false,
}: DesktopShellProps) {
  const t = useTranslations('navigation');
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);

  return (
    // Publish the content column's left offset (sidebar width + gap) as a CSS
    // var so viewport-fixed descendants (the floating Generate capsule) can
    // center over the content column instead of the whole viewport. When the
    // sidebar is collapsed the content keeps a small left gutter (for the
    // top-left reveal button), so the offset is that gutter, not 0. The stacked
    // floating footer only exists below xl (< 1728px), so the max-w container
    // adds no side margin there and this px offset is exact.
    //
    // `bare` (< lg) swaps every wrapper to `display: contents` so the boxes
    // produce no layout — the children render exactly as if unwrapped — but the
    // React element tree stays identical to the desktop one, so `children` keep
    // their identity (and state) when the viewport crosses `lg`.
    <div
      className={bare ? 'contents' : 'flex-1 min-h-0 w-full overflow-hidden bg-muted/30'}
      style={
        bare
          ? undefined
          : ({
              '--content-inset-left': collapsed
                ? `${COLLAPSED_LEFT_GUTTER}px`
                : `${SIDEBAR_WIDTH_EXPANDED + 16}px`,
              // Trailing inset matching the collapsed symmetric gutter (see the
              // page scroll containers' `pr-14`). Only the collapsed state has a
              // right gutter; with the sidebar expanded the content column
              // reaches the window's right edge, so the right inset is 0. A
              // viewport-fixed, centered overlay (the floating Generate capsule)
              // pads BOTH sides by these insets to center over the content
              // column instead of the raw viewport.
              '--content-inset-right': collapsed
                ? `${COLLAPSED_LEFT_GUTTER}px`
                : '0px',
            } as CSSProperties)
      }
    >
      <div className={bare ? 'contents' : 'relative h-full w-full'}>
        {/* Sidebar reveal — top-left, wide screens only, shown only while the
            sidebar is collapsed. Mirrors the Apple app, which moved this
            affordance out of the floating bottom bar into the leading top-left
            corner (a standard toolbar position). It sits in the empty margin to
            the left of the page's own leading title; tapping it brings the
            sidebar back. Below `lg` (`bare`) there is no sidebar, so it is
            omitted — the bottom bar is the only navigation there. */}
        {collapsed && !bare && (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={t('toggleSidebar')}
            // Dark glass circle matching the hero ⋮ menu button on the trailing
            // edge, so the two top-corner affordances read as a pair.
            className="fixed left-4 top-3 z-40 flex h-9 w-9 items-center justify-center rounded-full bg-background/60 text-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-background/80"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}
        {/* Full-bleed hero wash, painted BEHIND both columns (project home
            only). It spans the whole shell width — under the sidebar, the gap
            and the content — so the teal reads as one gradient sliding under
            the navigation. The frosted-glass sidebar (below) blurs the portion
            beneath it; the transparent content column shows it directly. */}
        {showTopWash && !bare && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-[220px]"
            style={{ background: HERO_WASH_GRADIENT }}
          />
        )}
        {/* `relative` lifts the columns above the absolute wash in paint order
            so the sidebar's backdrop-filter samples the wash as its backdrop. */}
        {/* The right-hand content column must reach the window's top/right/bottom
            edges so its scrollbar pins to the window edge and scrolled content
            clips at the window, not at an inset box. The sidebar keeps its
            floating-card look via its own `my-4 ml-4` margins; `gap-4` still
            separates columns (incl. account's 3-pane).
            When the sidebar is collapsed a small LEFT gutter is reserved so the
            page's own leading content (titles, back chevrons, the concept
            editor's top bar) clears the floating top-left sidebar-reveal button
            instead of sitting under it — mirroring Apple, where that button is a
            leading toolbar item that pushes the title right rather than overlaps
            it. The gutter is far narrower than the expanded sidebar, so
            collapsing still widens the content. The reserve is LEFT-only on
            purpose: it keeps the scroll column flush to the window's right edge
            so the scrollbar pins to the edge. Pages that center their own
            `mx-auto` content add a matching RIGHT padding on their own scroll
            container to re-center, without pulling the scrollbar inward (see
            ProjectDesktopLayout `main`). */}
        <div
          className={
            bare
              ? 'contents'
              : `relative flex h-full gap-4${collapsed ? ' pl-14' : ''}`
          }
        >
          {!collapsed && !bare && (
            // Frosted glass: translucent fill + backdrop-blur so the teal hero
            // wash painted behind the shell shows through softened (iOS Liquid
            // Glass direction). On pages without the wash it reads as a clean
            // light frosted card over the muted background.
            <aside
              className="flex-shrink-0 bg-background/60 backdrop-blur-2xl rounded-2xl border border-background shadow-[0_2px_12px_0_rgb(0_0_0_/_0.08)] my-4 ml-4 overflow-hidden flex flex-col"
              style={{ width: SIDEBAR_WIDTH_EXPANDED }}
            >
              {sidebar}
            </aside>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
